import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal, TemplateRef, WritableSignal } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription } from 'rxjs';
import { catchError, debounceTime, map, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  DsBannerState,
  DsButtonVariants,
  DsChipColorVariant,
  DsModalService,
  DsModalVariant,
  DsTooltipPosition,
  Icons,
  IconScale,
  IconStyles,
} from '@levelaccess/design-system';
import { FormBuilder, FormGroup, UntypedFormControl } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { HttpErrorResponse } from '@angular/common/http';
import { ITableColumn, ITableConfig, ITableRow } from '../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { IMobileApp, IMobileError } from '../../../../../../shared/interfaces/mobile-app.interface';
import { AclSecurityAdapter } from '../../../../services/acl.service';
import { FunctionalArea, SecureAction } from '../../../../../../shared/constants/security';
import { UserAclService } from '../../../../services/user-acl.service';
import { TranslateService } from '../../../../translate/translate.service';
import { MobileAppUploadStatus } from '../../../../../../shared/constants/mobile-app';
import { IDSChipCellConfig } from '../../../../components/table/ngb-table/cells/ds-chip-cell/ds-chip-cell.component';
import { NgbTableUtilities } from '../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { IHttpErrorResponse } from '../../../../interfaces/error.interface';
import { AngularUtility } from '../../../../utility/angular.utility';
import { MobileAppTestingService } from '../../../../services/mobile-app-testing.service';
import { MobileAppUploadNotificationService } from '../../../../services/mobile-app-upload-notification.service';
import { RemoveAppModalComponent } from '../remove-app-modal/remove-app-modal.component';
import {
  $digitalProperty,
  $digitalPropertyType,
  DigitalPropertyTypeNames,
} from '../../../../../../shared/constants/digital-properties';
import {
  MimeTypes,
  SupportedMobileAndroidExtensions,
  SupportedMobileAndroidMimeTypes,
  SupportedMobileIOSExtensions,
  SupportedMobileIOSExtensionsMac,
  SupportedMobileIOSMimeTypes,
} from '../../../../../../shared/constants/mime-type';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';
import { RequiredSecurities } from '../../../../../../shared/constants/required-securities';
import { IRequiredSecurity } from '../../../../../../shared/interfaces/security.interface';
import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { MOBILE_EMULATOR_MAX_FILE_SIZE, MOBILE_EMULATOR_MAX_FILE_SIZE_DECIMALS } from '../../../../../../shared/constants/mobile';
import { IDigitalPropertyDetails } from '../../../../../../shared/interfaces/digital-property.interface';
import { PULLING_INTERVAL } from '../../../../shared/constants';
import { BannerType as EditPermissionsBannerType } from '../../../../components/users/edit-user/edit-permissions-banner/edit-permissions-banner.component';
import { $workspace } from '../../../../../../shared/constants/workspace';
import { MobileErrorHandler } from '../../../../services/mobile-error-handler';
import { DetectUtility } from '../../../../utility/detect.utility';
import { SharedTextUtility } from '../../../../../../shared/utils/text.utility';
import { CommonUtility } from '../../../../utility/common.utility';
import { errorMessagesNames } from '../../../../../../shared/constants/errors';

enum TableColumn {
  APP_NAME = 'appName',
  PLATFORM = 'platform',
  APP_VERSION = 'appVersion',
  STATUS = 'status',
  ACTIONS = 'actions',
}

enum FormField {
  appUpload = 'app-upload',
}

@Component({
  selector: 'app-emulator-app-table',
  templateUrl: './emulator-app-table.component.html',
  styleUrl: './emulator-app-table.component.scss',
  standalone: false,
})
export class EmulatorAppTableComponent implements OnInit, OnDestroy {
  @Input() emptyState: TemplateRef<any> = null;
  @Input() digitalProperty: IDigitalPropertyDetails;

  @Output() mobileAppsLoaded: EventEmitter<IMobileApp[]> = new EventEmitter<IMobileApp[]>();

  public readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
  public readonly Icons: typeof Icons = Icons;
  public readonly IconScale: typeof IconScale = IconScale;
  public readonly IconStyles: typeof IconStyles = IconStyles;
  public readonly EditPermissionsBannerType: typeof EditPermissionsBannerType = EditPermissionsBannerType;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly FORM_FIELDS: typeof FormField = FormField;

  public readonly MAX_GB: string = SharedCommonUtility.formatBytes(
    MOBILE_EMULATOR_MAX_FILE_SIZE,
    MOBILE_EMULATOR_MAX_FILE_SIZE_DECIMALS,
  ).replace(/ /g, '');
  private subscription: Subscription = new Subscription();
  private loadData$: BehaviorSubject<void> = new BehaviorSubject(null);
  public tableConfig: ITableConfig;
  public tableRows$: Observable<ITableRow[]>;
  public mobileApps$: Observable<IMobileApp[]>;
  public createMobileApps: IRequiredSecurity;

  public formId: string;
  public form: FormGroup;

  public $fileError: WritableSignal<string | null> = signal(null);

  public acceptedExtensions: string[] = [];
  public acceptedExtensionsFormatted: string = '';
  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;

  constructor(
    private translateService: TranslateService,
    private userAclService: UserAclService,
    private mobileAppTestingService: MobileAppTestingService,
    private mobileAppUploadNotificationService: MobileAppUploadNotificationService,
    private mobileErrorHandler: MobileErrorHandler,
    private modalService: DsModalService,
    private formBuilder: FormBuilder,
  ) {
    this.tableConfig = this.buildTableConfig();
    this.createMobileApps = RequiredSecurities.AT_Mobile_Apps_Create;
    this.form = this.formBuilder.group({
      [FormField.appUpload]: new UntypedFormControl(null),
    });
  }

  private getAcceptedExtensions(): string[] {
    switch (this.digitalProperty[$digitalProperty.type][$digitalPropertyType.name]) {
      case DigitalPropertyTypeNames.android:
        return SupportedMobileAndroidExtensions;
      case DigitalPropertyTypeNames.ios:
        return DetectUtility.isMac() ? SupportedMobileIOSExtensionsMac : SupportedMobileIOSExtensions;
      default:
        return [];
    }
  }

  private loadMobileApps(): Observable<IMobileApp[]> {
    return this.loadData$.pipe(
      switchMap(() => {
        return this.mobileAppTestingService.getMobileApps(
          this.digitalProperty[$digitalProperty.workspace][$workspace._id],
          this.digitalProperty[$digitalProperty._id],
        );
      }),
      tap((mobileApps: IMobileApp[]) => {
        const [uploadError]: IMobileError[] = mobileApps.map((app: IMobileApp): IMobileError => app.uploadError);
        if (SharedCommonUtility.notNullish(uploadError)) {
          this.mobileErrorHandler.handleError('error_while_uploading_mobile_app', uploadError, EmulatorAppTableComponent.name);
        }

        this.mobileAppsLoaded.emit(mobileApps);
        this.mobileAppUploadNotificationService.handlePollResult(this.digitalProperty[$digitalProperty._id], mobileApps);
      }),
      catchError((response: IHttpErrorResponse) =>
        this.mobileErrorHandler.handleResponse$('mobile_apps_load_error', response, EmulatorAppTableComponent.name),
      ),
      AngularUtility.shareRef(),
    );
  }

  private buildTableConfig(): ITableConfig {
    const columns: Record<string, ITableColumn> = {
      [TableColumn.APP_NAME]: {
        translationKey: 'app_name',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '30%',
        },
      },
      [TableColumn.PLATFORM]: {
        translationKey: 'platform',
        styles: {
          width: '20%',
        },
      },
      [TableColumn.APP_VERSION]: {
        translationKey: 'app_version',
        styles: {
          width: '20%',
        },
      },
      [TableColumn.STATUS]: {
        translationKey: 'status',
        styles: {
          width: '20%',
        },
      },
      [TableColumn.ACTIONS]: {
        translationKey: 'table_column_actions',
        styles: {
          width: '10%',
        },
      },
    };

    return {
      columns: columns,
      caption: this.translateService.instant('mobile_testing_emulator_table'),
    };
  }

  private buildTableRows(): Observable<ITableRow[]> {
    return this.mobileApps$.pipe(
      withLatestFrom(this.hasPermsToRemoveMobileApps$),
      map(([mobileApps, hasPermsToRemoveMobileApps]: [IMobileApp[], boolean]) =>
        mobileApps.map((mobileApp: IMobileApp) => this.buildTableRow(mobileApp, hasPermsToRemoveMobileApps)),
      ),
      startWith([]),
    );
  }

  private buildTableRow(mobileApp: IMobileApp, hasPermsToRemoveMobileApps: boolean): ITableRow {
    const canRemoveMobileApp: boolean = hasPermsToRemoveMobileApps && mobileApp.openSessionsCount === 0;

    const statusChipConfig: IDSChipCellConfig = this.getUploadStatusCellConfig(mobileApp.uploadStatus);
    return {
      data: {
        [TableColumn.APP_NAME]:
          mobileApp.uploadStatus === MobileAppUploadStatus.PENDING
            ? NgbTableUtilities.textCell({
                text: this.translateService.instant('mobile_app_upload_pending'),
                colSpan: 3,
                classes: ['inactive'],
              })
            : NgbTableUtilities.textCell({
                text: mobileApp.name,
              }),
        [TableColumn.PLATFORM]:
          mobileApp.uploadStatus === MobileAppUploadStatus.PENDING
            ? null
            : NgbTableUtilities.textCell({
                text: mobileApp.platform,
              }),
        [TableColumn.APP_VERSION]:
          mobileApp.uploadStatus === MobileAppUploadStatus.PENDING
            ? null
            : NgbTableUtilities.textCell({
                text: mobileApp.version,
              }),
        [TableColumn.STATUS]:
          mobileApp.uploadStatus === MobileAppUploadStatus.EXPIRED
            ? NgbTableUtilities.tooltipWrapperCell<IDSChipCellConfig>({
                reference: NgbTableUtilities.DSChipCell(statusChipConfig),
                tooltip: {
                  text: 'mobile_app_file_expiration_tooltip_message',
                  translate: true,
                  tooltipPlacement: DsTooltipPosition.bottom,
                  innerRefStyles: { padding: '3px' }, // this is supposed to be ds-spacing-10 but it looks off
                },
              })
            : NgbTableUtilities.DSChipCell(statusChipConfig),
        [TableColumn.ACTIONS]: NgbTableUtilities.dropdownCell({
          ellipsisIcon: true,
          dropdownItems: [
            ...(canRemoveMobileApp
              ? [
                  {
                    label: 'remove',
                    clickListener: (): void => {
                      this.openRemovingModal(mobileApp);
                    },
                    translate: true,
                  },
                ]
              : []),
          ],
        }),
      },
    };
  }

  private getUploadStatusCellConfig(uploadStatus: MobileAppUploadStatus): IDSChipCellConfig {
    switch (uploadStatus) {
      case MobileAppUploadStatus.PENDING:
        return {
          text: this.translateService.instant('mobile_app_upload_status_in_progress'),
          chipColorVariant: DsChipColorVariant.LightBlue,
        };
      case MobileAppUploadStatus.UPLOADED:
        return {
          text: this.translateService.instant('mobile_app_upload_status_uploaded'),
          chipColorVariant: DsChipColorVariant.Green,
        };
      case MobileAppUploadStatus.EXPIRED:
        return {
          text: this.translateService.instant('mobile_app_upload_status_expired'),
          chipColorVariant: DsChipColorVariant.LightGray,
        };
      case MobileAppUploadStatus.FAILED:
        return {
          text: this.translateService.instant('mobile_app_upload_status_failed'),
          chipColorVariant: DsChipColorVariant.Red,
        };
      default:
        return null;
    }
  }

  public get hasPermsToCreateMobileApps$(): Observable<boolean> {
    return this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useFunctionalActions({
            [FunctionalArea.at_mobile_apps]: [SecureAction.create],
          })
          .useWorkspaceFromUser()
          .useDigitalPropertyFromUser()
          .check(),
      ),
    );
  }

  private get hasPermsToRemoveMobileApps$(): Observable<boolean> {
    return this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useFunctionalActions({
            [FunctionalArea.at_mobile_apps]: [SecureAction.delete],
          })
          .useWorkspaceFromUser()
          .useDigitalPropertyFromUser()
          .check(),
      ),
    );
  }

  private uploadMobileApp(file: File): Observable<IMobileApp> {
    const formData: FormData = new FormData();
    formData.set('file', file, file.name);

    return this.mobileAppTestingService.uploadMobileApp(
      this.digitalProperty[$digitalProperty.workspace][$workspace._id],
      this.digitalProperty[$digitalProperty._id],
      formData,
    );
  }

  private removeMobileApp(mobileApp: IMobileApp): Observable<void> {
    return this.mobileAppTestingService
      .removeMobileApp(
        this.digitalProperty[$digitalProperty.workspace][$workspace._id],
        this.digitalProperty[$digitalProperty._id],
        mobileApp.id,
      )
      .pipe(
        catchError((response: IHttpErrorResponse) =>
          this.mobileErrorHandler.handleResponse$('mobile_apps_remove_error', response, EmulatorAppTableComponent.name),
        ),
      );
  }

  private validateFileSize(event: Event, file: File): boolean {
    const isValid: boolean = file.size < MOBILE_EMULATOR_MAX_FILE_SIZE;
    if (!isValid) {
      this.clearInput(event);
      this.$fileError.set(this.translateService.instant('the_uploaded_mobile_app_is_too_large', this.MAX_GB));
    }
    return isValid;
  }

  private validateFileType(event: Event, file: File): boolean {
    let errorMessage: string = null;

    switch (this.digitalProperty[$digitalProperty.type][$digitalPropertyType.name]) {
      case DigitalPropertyTypeNames.android:
        if (!SupportedMobileAndroidMimeTypes.includes(file.type as MimeTypes)) {
          errorMessage = this.translateService.instant('the_uploaded_mobile_app_is_the_wrong_type', [
            this.acceptedExtensionsFormatted,
            this.digitalProperty[$digitalProperty.name],
          ]);
        }
        break;
      case DigitalPropertyTypeNames.ios:
        // ios apps sometimes don't contain mimetypes
        if (file.type !== '' && !SupportedMobileIOSMimeTypes.includes(file.type as MimeTypes)) {
          errorMessage = this.translateService.instant('the_uploaded_mobile_app_is_the_wrong_type', [
            this.acceptedExtensionsFormatted,
            this.digitalProperty[$digitalProperty.name],
          ]);
        }
        break;
      default:
        break;
    }

    this.clearInput(event);
    this.$fileError.set(errorMessage);

    return SharedCommonUtility.isNullishOrEmpty(errorMessage);
  }

  private setupPolling(): void {
    this.subscription.add(
      this.mobileApps$.pipe(debounceTime(PULLING_INTERVAL.SLOW)).subscribe((): void => this.loadData$.next()),
    );
  }

  private clearInput(event: Event): void {
    (event.target as HTMLInputElement).value = null;
  }

  public onCloseFileError(): void {
    this.$fileError.set(null);
  }

  public openRemovingModal(mobileApp: IMobileApp): void {
    const modalRef: NgbModalRef = this.modalService.open(RemoveAppModalComponent, DsModalVariant.center) as NgbModalRef;

    const component: RemoveAppModalComponent = modalRef.componentInstance;
    this.subscription.add(
      component.removeAppConfirmed
        .pipe(
          take(1),
          switchMap((): Observable<void> => {
            return this.removeMobileApp(mobileApp);
          }),
        )
        .subscribe((): void => {
          this.loadData$.next();
        }),
    );
  }

  public onFileUpload(event: Event): void {
    event.preventDefault();
    const element: HTMLInputElement = event.currentTarget as HTMLInputElement;
    const file: File = Array.from(element.files)?.[0];
    if (SharedCommonUtility.notNullish(file)) {
      this.processMobileApp(event, file);
    }
  }

  public processMobileApp(event: Event, file: File): void {
    if (!this.validateFileSize(event, file) || !this.validateFileType(event, file)) {
      return;
    }
    this.subscription.add(
      this.uploadMobileApp(file)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            const mobileErrorMessage: string | null = this.mobileErrorHandler.getMobileErrorMessage(error);

            if (SharedCommonUtility.notNullish(mobileErrorMessage)) {
              this.$fileError.set(mobileErrorMessage);
            } else if (CommonUtility.extractHTTPErrorName(error) === errorMessagesNames.MobileAppBundleIdMismatch) {
              this.$fileError.set(this.translateService.instant('mobile_app_bundle_id_mismatch_upload_error'));
            } else {
              this.mobileErrorHandler.handleResponse('error_while_uploading_mobile_app', error, EmulatorAppTableComponent.name);
            }
            this.clearInput(event);
            return EMPTY;
          }),
        )
        .subscribe((mobileApp: IMobileApp) => {
          this.mobileAppUploadNotificationService.beginTracking(
            this.digitalProperty[$digitalProperty.workspace][$workspace._id],
            this.digitalProperty[$digitalProperty._id],
            mobileApp.id,
          );
          this.loadData$.next();
        }),
    );
  }

  public ngOnInit(): void {
    this.acceptedExtensions = this.getAcceptedExtensions();
    this.acceptedExtensionsFormatted = SharedTextUtility.formatJoin(this.acceptedExtensions);
    this.mobileApps$ = this.loadMobileApps();
    this.tableRows$ = this.buildTableRows();
    this.setupPolling();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
